import React, { type JSX } from 'react';

import { useConfirm } from '@fiji/common/src/hooks';
import { CustomModal } from 'components';
import { ModalActions } from 'components/CustomModal';
import { useHandleUpdate } from './hooks';

import { useAppDispatch } from '@fiji/common/src/app/store';
import { resetDeviceProperties, setUpdateProperties } from '@fiji/common/src/features/deviceManagement/deviceSlice';
import { DateUpdateModal, LocationUpdateModal, ModalHierarchy, NameOrDescriptionModal, UpdateTagsModal } from './';
import { useCreateTagMutation } from '@fiji/common/src/features/TagManagement/tagManagementApi';
import { t } from 'i18next';
import { useTextFieldError } from 'hooks';

// ─── pure helpers ─────────────────────────────────────────────────────────────
// Extracted outside the component so their internal branches do not count
// toward UpdateModal's cyclomatic complexity.

const TEXT_FIELD_KEYS = new Set(['name', 'description']);

function isSaveDisabled(
    baseDisabled: boolean,
    isTagCreating: boolean,
    isLoading: boolean,
    isTextField: boolean,
    hasTextError: boolean
): boolean {
    return baseDisabled || isTagCreating || isLoading || (isTextField && hasTextError);
}

function getSaveLabel(isLoading: boolean): string {
    return isLoading ? t('COMMON:SAVING') : t('COMMON:SAVE_LABEL');
}

function resolveInitialValue(key: string, value: any): { key: string; value: any } | null {
    if (!value) return null;
    if (key === 'groupId') {
        return { key: 'groupId', value: { id: value.groupId, name: value.groupName } };
    }
    return { key, value: Array.isArray(value) ? value.map((item: any) => item.name) : value };
}

function getSubHeader(isSubHeader: boolean, name: string | undefined): string | undefined {
    return isSubHeader ? name : undefined;
}

function extractData(data: any): { key: string; value: any; id: any; name: string | undefined } {
    const d = data ?? {};
    return { key: d.key ?? '', value: d.value, id: d.id, name: d.name };
}

function resolveModalHeader(custom: string | undefined, handle: (k: string) => string, key: string): string {
    return custom ?? handle(key);
}

function buildModalActionHandler(
    onClick: (args?: any) => void,
    onCancel: () => void
): (action: boolean, args?: any) => void {
    return (action, args): void => {
        if (action) {
            onClick(args);
        } else {
            onCancel();
        }
    };
}

const EMPTY_CONTENT: JSX.Element = <></>;
function renderContent(key: string, map: Record<string, JSX.Element>): JSX.Element {
    return map[key] ?? EMPTY_CONTENT;
}

function applyInitialValue(key: string, value: any, dispatch: (a: any) => void): void {
    const resolved = resolveInitialValue(key, value);
    if (resolved) dispatch(setUpdateProperties(resolved));
}

// ─────────────────────────────────────────────────────────────────────────────

export const UpdateModal = React.forwardRef((props: any, ref): JSX.Element => {
    // React always provides an object for props — no `?? {}` needed.
    const { allProperties, header, isSubHeader = false } = props;
    const dispatch = useAppDispatch();
    const { data, isVisible, onClick, onCancel } = useConfirm();
    const { key, value, id, name } = extractData(data);
    const [createTag, { isLoading: isTagCreating }] = useCreateTagMutation();
    const { handleDisable, handleModalHeader, handleModalType, isLoading, handleSubmit } = useHandleUpdate();
    const { hasTextError, makeErrorHandler, resetErrors } = useTextFieldError();

    const isTextField = TEXT_FIELD_KEYS.has(key);
    const isActionLoading = isTagCreating || isLoading;

    const updateModalActions: ModalActions[] = [
        {
            key: 'cancel',
            label: t('COMMON:CANCEL'),
            close: true,
            variant: 'outlined',
            handleClick: (): void => {
                onCancel();
                dispatch(resetDeviceProperties());
            },
        },
        {
            key: 'save',
            label: getSaveLabel(isLoading),
            disabled: isSaveDisabled(handleDisable(data), isTagCreating, isLoading, isTextField, hasTextError),
            isLoading: isActionLoading,
            variant: 'contained',
            handleClick: (): void => {
                handleSubmit(key, onCancel, id);
            },
        },
    ];

    const handleModalAction = buildModalActionHandler(onClick, onCancel);

    React.useImperativeHandle(
        ref,
        () => ({ handleModalAction: (args0: boolean, args1?: any) => handleModalAction(args0, args1) }),
        []
    );

    const contentMap: Record<string, JSX.Element> = {
        groupId: <ModalHierarchy />,
        batteryInstallDate: <DateUpdateModal type="batteryInstallDate" apiData={allProperties} />,
        deviceInstallDate: <DateUpdateModal type="deviceInstallDate" apiData={allProperties} />,
        tags: <UpdateTagsModal data={data} createHandler={createTag} />,
        name: <NameOrDescriptionModal type="name" handleTextError={makeErrorHandler('name')} />,
        description: <NameOrDescriptionModal type="description" handleTextError={makeErrorHandler('description')} />,
        location: <LocationUpdateModal />,
    };

    React.useEffect(() => {
        resetErrors();
    }, [key, isVisible, resetErrors]);

    React.useEffect(() => {
        applyInitialValue(key, value, dispatch);
    }, [data, isVisible]);

    return (
        <CustomModal
            isOpen={isVisible}
            isLoading={isLoading}
            actions={updateModalActions}
            actionsDivider
            header={resolveModalHeader(header, handleModalHeader, key)}
            subHeader={getSubHeader(isSubHeader, name)}
            type={handleModalType(key)}
        >
            {renderContent(key, contentMap)}
        </CustomModal>
    );
});
